import { useEffect, useState } from 'react';
import { usePositionStore } from '../stores/positionStore';
import { useAuthStore } from '../stores/authStore';
import socketManager from '../lib/socket';
import {
  TrendingUp,
  TrendingDown,
  X,
  Shield,
  Target,
  ExternalLink,
  Loader2,
  Filter,
  Users
} from 'lucide-react';

function PositionCard({ position, onClose, onUpdateStopLoss, onUpdateTakeProfit }) {
  const [showSettings, setShowSettings] = useState(false);
  const [stopLossValue, setStopLossValue] = useState(position.stopLoss?.triggerPrice || 0);
  const [takeProfitValue, setTakeProfitValue] = useState(position.takeProfit?.triggerPrice || 0);

  const copyConfig = position.copyConfigId;
  const copyLabel =
    (copyConfig && typeof copyConfig === 'object' && (copyConfig.traderName || copyConfig.traderAddress))
      ? `${copyConfig.traderName || 'Trader'}${copyConfig.traderAddress ? ` (${copyConfig.traderAddress.slice(0, 6)}…${copyConfig.traderAddress.slice(-4)})` : ''}`
      : (position.copiedFrom?.traderAddress
        ? `${position.copiedFrom.traderAddress.slice(0, 6)}…${position.copiedFrom.traderAddress.slice(-4)}`
        : null);

  let avgPrice = Number(position.avgPrice) || 0;
  const currentPrice = Number(position.currentPrice) || 0;
  let size = Number(position.size) || 0;
  
  // Fallback calculations for missing data
  // If avgPrice is 0 but we have initialValue and size, calculate it
  if (avgPrice === 0 && size > 0 && Number(position.initialValue) > 0) {
    avgPrice = Number(position.initialValue) / size;
  }
  // If size is 0 but we have initialValue and avgPrice, calculate it
  if (size === 0 && avgPrice > 0 && Number(position.initialValue) > 0) {
    size = Number(position.initialValue) / avgPrice;
  }
  // If avgPrice is still 0, try using currentPrice as fallback (better than showing $0)
  if (avgPrice === 0 && currentPrice > 0) {
    avgPrice = currentPrice;
  }
  const currentValue = Number.isFinite(Number(position.currentValue))
    ? Number(position.currentValue)
    : size * currentPrice;
  const costBasis = Number.isFinite(Number(position.initialValue))
    ? Number(position.initialValue)
    : size * avgPrice;
  
  // For closed/expired positions, use realizedPnL if available
  const isClosed = position.status !== 'open';
  const realizedPnL = Number.isFinite(Number(position.realizedPnL)) ? Number(position.realizedPnL) : 0;
  const unrealizedPnL = Number.isFinite(Number(position.unrealizedPnL))
    ? Number(position.unrealizedPnL)
    : currentValue - costBasis;
  
  // For expired/resolved positions, calculate PnL based on resolution
  // If market resolved in our favor (outcome won), we get $1 per share
  // If market resolved against us, we get $0 per share
  let displayPnL = isClosed ? realizedPnL : unrealizedPnL;
  
  // If realizedPnL is 0 but we have a closed position with cost basis, calculate it
  if (isClosed && realizedPnL === 0 && costBasis > 0) {
    // Check if this is a winning resolution (position at $1) or losing ($0)
    if (position.closeReason === 'market_resolved' || position.closeReason === 'expired') {
      // Use currentPrice at close time if available, otherwise check resolution
      const resolutionPrice = currentPrice;
      if (resolutionPrice >= 0.95) {
        // Won - received $1 per share
        displayPnL = (size * 1.0) - costBasis;
      } else if (resolutionPrice <= 0.05) {
        // Lost - received $0 per share
        displayPnL = -costBasis;
      } else {
        // Somewhere in between (partial or sold before resolution)
        displayPnL = currentValue - costBasis;
      }
    } else {
      // Regular close - use unrealized as the realized
      displayPnL = unrealizedPnL;
    }
  }
  
  const pnlPercent = Number.isFinite(Number(position.unrealizedPnLPercent))
    ? Number(position.unrealizedPnLPercent)
    : (costBasis > 0 ? (displayPnL / costBasis) * 100 : 0);
  const isProfit = displayPnL >= 0;

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate">{position.market?.question}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`badge ${position.market?.outcome === 'Yes' ? 'badge-success' : 'badge-danger'}`}>
              {position.market?.outcome}
            </span>
            {position.source === 'sentinel' && (
              <span className="badge bg-purple-600 text-white">🤖 Sentinel</span>
            )}
            {position.source === 'whale' && (
              <span className="badge bg-blue-600 text-white">🐋 Whale</span>
            )}
            {position.source === 'copy_trade' && (
              <span className="badge badge-info">📋 Copy</span>
            )}
            {!position.source && position.copiedFrom?.traderAddress && (
              <span className="badge badge-info">Copied</span>
            )}
            {copyLabel && (
              <span className="badge badge-info">{copyLabel}</span>
            )}
            {/* Status badge for non-open positions */}
            {position.status && position.status !== 'open' && (
              <span className={`badge ${
                position.status === 'closed' ? 'bg-gray-600' :
                position.status === 'stopped_out' ? 'bg-red-600' :
                position.status === 'take_profit' ? 'bg-emerald-600' :
                position.status === 'market_resolved' ? 'bg-yellow-600' :
                'bg-gray-600'
              } text-white`}>
                {position.status === 'stopped_out' ? '🛑 Stopped' :
                 position.status === 'take_profit' ? '🎯 TP Hit' :
                 position.status === 'market_resolved' ? '📊 Resolved' :
                 position.status === 'closed' ? '✓ Closed' :
                 position.status}
              </span>
            )}
            <a
              href={`https://polymarket.com/event/${position.market?.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
            >
              View <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
        
        <div className="text-right ml-4">
          <p className={`text-xl font-bold ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
            {isProfit ? '+' : ''}{pnlPercent.toFixed(1)}%
          </p>
          <p className="text-sm text-dark-400">
            {position.status === 'open' ? 'Unrealized: ' : 'Realized: '}
            {(displayPnL >= 0 ? '+' : '')}${displayPnL.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Position Details */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Size</p>
          <p className="font-medium">{size.toFixed(2)}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Avg Price</p>
          <p className="font-medium">${avgPrice.toFixed(2)}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Current</p>
          <p className="font-medium">${currentPrice.toFixed(2)}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Value</p>
          <p className="font-medium">${currentValue.toFixed(2)}</p>
        </div>
      </div>

      {/* Stop Loss / Take Profit Indicators */}
      <div className="flex items-center gap-4 mb-4">
        {position.stopLoss?.enabled && (
          <div className="flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-red-400" />
            <span className="text-dark-400">SL:</span>
            <span className="text-red-400">${position.stopLoss.triggerPrice?.toFixed(2)}</span>
          </div>
        )}
        {position.takeProfit?.enabled && (
          <div className="flex items-center gap-2 text-sm">
            <Target className="w-4 h-4 text-emerald-400" />
            <span className="text-dark-400">TP:</span>
            <span className="text-emerald-400">${position.takeProfit.triggerPrice?.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Actions - only show for open positions */}
      {position.status === 'open' && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="btn btn-secondary flex-1"
          >
            {showSettings ? 'Hide Settings' : 'Risk Settings'}
          </button>
          <button
            onClick={() => onClose(position._id)}
            className="btn btn-danger flex-1"
          >
            Close Position
          </button>
        </div>
      )}
      
      {/* Closed position info */}
      {position.status !== 'open' && (
        <div className="text-center py-2 text-sm text-dark-400">
          {position.closedAt && (
            <span>Closed: {new Date(position.closedAt).toLocaleDateString()}</span>
          )}
          {position.closeReason && (
            <span className="ml-2">• {position.closeReason.replace(/_/g, ' ')}</span>
          )}
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="mt-4 pt-4 border-t border-dark-700 space-y-4 animate-fade-in">
          {/* Stop Loss */}
          <div className="p-3 bg-dark-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-400" />
                Stop Loss
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={position.stopLoss?.enabled}
                  onChange={(e) => onUpdateStopLoss(position._id, {
                    enabled: e.target.checked,
                    type: 'percentage',
                    value: 20
                  })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
            {position.stopLoss?.enabled && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={stopLossValue}
                  onChange={(e) => setStopLossValue(parseFloat(e.target.value))}
                  className="input flex-1"
                  step="0.01"
                  min="0"
                  max="1"
                />
                <button
                  onClick={() => onUpdateStopLoss(position._id, {
                    enabled: true,
                    type: 'fixed',
                    value: position.avgPrice - stopLossValue
                  })}
                  className="btn btn-secondary"
                >
                  Update
                </button>
              </div>
            )}
          </div>

          {/* Take Profit */}
          <div className="p-3 bg-dark-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-400" />
                Take Profit
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={position.takeProfit?.enabled}
                  onChange={(e) => onUpdateTakeProfit(position._id, {
                    enabled: e.target.checked,
                    type: 'percentage',
                    value: 50
                  })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
            {position.takeProfit?.enabled && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={takeProfitValue}
                  onChange={(e) => setTakeProfitValue(parseFloat(e.target.value))}
                  className="input flex-1"
                  step="0.01"
                  min="0"
                  max="1"
                />
                <button
                  onClick={() => onUpdateTakeProfit(position._id, {
                    enabled: true,
                    type: 'fixed',
                    value: takeProfitValue - position.avgPrice
                  })}
                  className="btn btn-secondary"
                >
                  Update
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Positions() {
  const { user } = useAuthStore();
  const {
    positions,
    summary,
    isLoading,
    fetchPositions,
    fetchSummary,
    updateStopLoss,
    updateTakeProfit,
    closePosition,
    updatePosition
  } = usePositionStore();
  
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const raw = localStorage.getItem('betmimic.positions.filters');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.statusFilter || 'all'; // Default to ALL positions
    } catch (_) {
      return 'all';
    }
  });
  const [traderFilter, setTraderFilter] = useState(() => {
    try {
      const raw = localStorage.getItem('betmimic.positions.filters');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.traderFilter || 'all';
    } catch (_) {
      return 'all';
    }
  });
  const [isClosing, setIsClosing] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        'betmimic.positions.filters',
        JSON.stringify({ statusFilter, traderFilter })
      );
    } catch (_) {
    }
  }, [statusFilter, traderFilter]);

  useEffect(() => {
    // Always fetch fresh data from server on mount/filter change
    fetchPositions(statusFilter);
    fetchSummary();
    
    // Auto-refresh every 60 seconds to catch any missed updates (reduced from 30s to prevent flicker)
    const autoRefreshInterval = setInterval(() => {
      fetchPositions(statusFilter, false); // silent refresh
      fetchSummary();
    }, 60000);
    
    // Subscribe to real-time updates
    if (user?._id) {
      socketManager.subscribeToPositions(user._id);

      // Debounced refresh - only refresh after events settle (not on every event)
      let refreshTimeout = null;
      const debouncedRefresh = () => {
        if (refreshTimeout) clearTimeout(refreshTimeout);
        refreshTimeout = setTimeout(() => {
          fetchPositions(statusFilter, false); // silent refresh to prevent flicker
          fetchSummary();
        }, 5000); // Wait 5s after last event before refreshing (increased from 2s)
      };

      // Just update local state from socket - don't spam server
      const onPositionUpdated = (position) => {
        updatePosition(position);
      };
      const onStopLoss = ({ position }) => {
        updatePosition(position);
        debouncedRefresh(); // Refresh after SL/TP events (status changes)
      };
      const onTakeProfit = ({ position }) => {
        updatePosition(position);
        debouncedRefresh(); // Refresh after SL/TP events (status changes)
      };
      const onTradeExecuted = () => debouncedRefresh();
      const onTradeFailed = () => debouncedRefresh();

      socketManager.on('positionUpdated', onPositionUpdated);
      socketManager.on('stopLossTriggered', onStopLoss);
      socketManager.on('takeProfitTriggered', onTakeProfit);
      socketManager.on('tradeExecuted', onTradeExecuted);
      socketManager.on('tradeFailed', onTradeFailed);

      return () => {
        clearInterval(autoRefreshInterval);
        if (refreshTimeout) clearTimeout(refreshTimeout);
        socketManager.off('positionUpdated', onPositionUpdated);
        socketManager.off('stopLossTriggered', onStopLoss);
        socketManager.off('takeProfitTriggered', onTakeProfit);
        socketManager.off('tradeExecuted', onTradeExecuted);
        socketManager.off('tradeFailed', onTradeFailed);
      };
    }
    
    // Cleanup interval even if no user
    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, [user?._id, statusFilter]);

  const handleClose = async (positionId) => {
    if (!confirm('Are you sure you want to close this position?')) return;
    
    setIsClosing(positionId);
    const result = await closePosition(positionId);
    setIsClosing(null);
    
    if (!result.success) {
      alert(result.error || 'Failed to close position');
    }
  };

  const handleUpdateStopLoss = async (positionId, data) => {
    await updateStopLoss(positionId, data);
  };

  const handleUpdateTakeProfit = async (positionId, data) => {
    await updateTakeProfit(positionId, data);
  };

  const getTraderOptionForPosition = (position) => {
    const copyConfig = position?.copyConfigId;
    const cfgObj = copyConfig && typeof copyConfig === 'object' ? copyConfig : null;
    const cfgId = cfgObj?._id || (typeof copyConfig === 'string' ? copyConfig : null);
    const cfgTraderAddress = cfgObj?.traderAddress;
    const cfgTraderName = cfgObj?.traderName;
    if (cfgId) {
      const shortAddr = cfgTraderAddress
        ? ` (${cfgTraderAddress.slice(0, 6)}…${cfgTraderAddress.slice(-4)})`
        : '';
      return {
        key: `config:${cfgId}`,
        label: `${cfgTraderName || 'Trader'}${shortAddr}`
      };
    }

    const addr = position?.copiedFrom?.traderAddress;
    if (addr) {
      return {
        key: `addr:${addr.toLowerCase()}`,
        label: `${addr.slice(0, 6)}…${addr.slice(-4)}`
      };
    }

    return null;
  };

  const traderOptions = (() => {
    const map = new Map();
    // Add source-based options first
    const sources = new Set();
    for (const p of positions || []) {
      if (p.source) sources.add(p.source);
    }
    // Add source options in order: Sentinel, Whale, Copy Trade
    if (sources.has('sentinel')) map.set('source:sentinel', '🤖 Sentinel');
    if (sources.has('whale')) map.set('source:whale', '🐋 Whale');
    if (sources.has('copy_trade')) map.set('source:copy_trade', '📋 Copy Trade');
    if (sources.has('manual')) map.set('source:manual', '✋ Manual');
    
    // Add trader address options
    for (const p of positions || []) {
      const opt = getTraderOptionForPosition(p);
      if (!opt) continue;
      if (!map.has(opt.key)) {
        map.set(opt.key, opt.label);
      }
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => {
        // Keep source options at top
        const aIsSource = a.value.startsWith('source:');
        const bIsSource = b.value.startsWith('source:');
        if (aIsSource && !bIsSource) return -1;
        if (!aIsSource && bIsSource) return 1;
        return (a.label || '').localeCompare(b.label || '');
      });
  })();

  const filteredPositions = (positions || [])
    .filter((p) => statusFilter === 'all' || (p.status || 'open') === statusFilter)
    .filter((p) => {
      if (traderFilter === 'all') return true;
      const [kind, value] = (traderFilter || '').split(':');
      if (!kind || !value) return true;

      if (kind === 'config') {
        const copyConfig = p?.copyConfigId;
        const cfgObj = copyConfig && typeof copyConfig === 'object' ? copyConfig : null;
        const cfgId = (cfgObj?._id || (typeof copyConfig === 'string' ? copyConfig : null))?.toString();
        return cfgId === value;
      }

      if (kind === 'addr') {
        const addr = p?.copiedFrom?.traderAddress?.toLowerCase();
        return !!addr && addr === value.toLowerCase();
      }

      if (kind === 'source') {
        return p?.source === value;
      }

      return true;
    });

  const selectedTraderLabel = traderFilter === 'all'
    ? 'All Traders'
    : (traderOptions.find((o) => o.value === traderFilter)?.label || 'Selected Trader');

  const selectedSummary = (() => {
    const s = {
      positions: filteredPositions.length,
      totalValue: 0,
      totalUnrealizedPnL: 0,
      totalRealizedPnL: 0
    };
    for (const p of filteredPositions) {
      const size = Number(p?.size) || 0;
      const currentPrice = Number(p?.currentPrice) || 0;
      const avgPrice = Number(p?.avgPrice) || 0;
      const currentValue = Number.isFinite(Number(p?.currentValue))
        ? Number(p.currentValue)
        : size * currentPrice;
      const costBasis = Number.isFinite(Number(p?.initialValue))
        ? Number(p.initialValue)
        : size * avgPrice;
      const unrealized = Number.isFinite(Number(p?.unrealizedPnL))
        ? Number(p.unrealizedPnL)
        : currentValue - costBasis;
      const realized = Number.isFinite(Number(p?.realizedPnL)) ? Number(p.realizedPnL) : 0;

      s.totalValue += currentValue || 0;
      s.totalUnrealizedPnL += unrealized || 0;
      s.totalRealizedPnL += realized || 0;
    }
    return s;
  })();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Positions</h1>
          <p className="text-dark-400">Manage your open and closed positions</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-dark-400">Total Value</p>
          <p className="text-2xl font-bold">${(summary?.totalValue || 0).toFixed(2)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-dark-400">Unrealized P&L</p>
          <p className={`text-2xl font-bold ${(summary?.totalUnrealizedPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {(summary?.totalUnrealizedPnL || 0) >= 0 ? '+' : ''}${(summary?.totalUnrealizedPnL || 0).toFixed(2)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-dark-400">Open Positions</p>
          <p className="text-2xl font-bold">{summary?.byStatus?.open || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-dark-400">Closed Positions</p>
          <p className="text-2xl font-bold">{(summary?.byStatus?.closed || 0) + (summary?.byStatus?.stopped_out || 0) + (summary?.byStatus?.take_profit || 0)}</p>
        </div>
      </div>

      {traderFilter !== 'all' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <p className="text-sm text-dark-400">Selected Trader</p>
            <p className="text-base font-semibold truncate">{selectedTraderLabel}</p>
          </div>
          <div className="card">
            <p className="text-sm text-dark-400">Positions</p>
            <p className="text-2xl font-bold">{selectedSummary.positions || 0}</p>
          </div>
          <div className="card">
            <p className="text-sm text-dark-400">Unrealized P&L</p>
            <p className={`text-2xl font-bold ${(selectedSummary.totalUnrealizedPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {(selectedSummary.totalUnrealizedPnL || 0) >= 0 ? '+' : ''}${(selectedSummary.totalUnrealizedPnL || 0).toFixed(2)}
            </p>
          </div>
          <div className="card">
            <p className="text-sm text-dark-400">Realized P&L</p>
            <p className={`text-2xl font-bold ${(selectedSummary.totalRealizedPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {(selectedSummary.totalRealizedPnL || 0) >= 0 ? '+' : ''}${(selectedSummary.totalRealizedPnL || 0).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-dark-400" />
        <select
          value={traderFilter}
          onChange={(e) => setTraderFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="all">All Traders</option>
          {traderOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <Filter className="w-4 h-4 text-dark-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="stopped_out">Stopped Out</option>
          <option value="take_profit">Take Profit</option>
          <option value="all">All</option>
        </select>
      </div>

      {/* Positions Grid */}
      {isLoading && positions.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
        </div>
      ) : filteredPositions.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredPositions.map(position => (
            <PositionCard
              key={position._id}
              position={position}
              onClose={handleClose}
              onUpdateStopLoss={handleUpdateStopLoss}
              onUpdateTakeProfit={handleUpdateTakeProfit}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <TrendingUp className="w-12 h-12 mx-auto mb-4 text-dark-500" />
          <h3 className="text-lg font-medium mb-2">No {statusFilter !== 'all' ? statusFilter : ''} Positions</h3>
          <p className="text-dark-400">
            {statusFilter === 'open' 
              ? 'Your open positions will appear here when you start copy trading.'
              : 'No positions match the selected filter.'}
          </p>
        </div>
      )}
    </div>
  );
}
